/**
 * AnonimizacaoPage — Brief 5 B5 Anonimização (Sprint Frontend Brief 5 · Tela 3).
 *
 * Wizard de criação (escopo → nível → evidência) + histórico por entidade, com o
 * lifecycle REAL de 3 POSTs: create (pending) → execute (in_progress, confirmação
 * dura/irreversível) → complete (completed). Tudo tenant-scoped (P1).
 *
 * Padrões: Opção A (capability-driven UI) · D-B defense-in-depth (gate próprio da
 * página). Gate: `lgpd-anonymization-execute` (tierMin Professional · requires
 * lgpd-requests-management).
 *
 * Contrato triangulado campo-a-campo (R15) — NÃO reusa o tipo drifted de
 * centro-direitos. `entityId` é coletado de verdade (obrigatório no BE);
 * `tenant-all` é bloqueado. Honest-defers visíveis (balde B): ImpactPreview real
 * (dry-run), co-signer Regulated e audit-config (opIdLabel/retention/notify).
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useMemo, useState } from 'react';

import {
  buildCreateRequest,
  initialAnonymizationForm,
  validateAnonymizationForm,
  type AnonymizationFormState,
} from '../features/dpo/anonimizacao/anonimizacao-form';
import { AnonymizationHistoryRow } from '../features/dpo/anonimizacao/AnonymizationHistoryRow';
import { AnonymizationPreviewPanel } from '../features/dpo/anonimizacao/AnonymizationPreviewPanel';
import { ExecuteAnonymizationModal } from '../features/dpo/anonimizacao/ExecuteAnonymizationModal';
import {
  ANONYMIZATION_LEVEL_META,
  ENTITY_TYPE_OPTIONS,
  type AnonymizationLevel,
  type AnonymizationWireItem,
} from '../features/dpo/anonimizacao/types';
import {
  useAnonymizationsByEntity,
  useCompleteAnonymization,
  useCreateAnonymization,
  useExecuteAnonymization,
  usePreviewAnonymization,
  type EntityRef,
} from '../hooks/use-anonymizations';
import { useCapability } from '../hooks/use-capability';

import '../features/dpo/anonimizacao/anonimizacao.css';

const LEVELS: readonly AnonymizationLevel[] = ['total', 'contextual', 'statistical'];

export function AnonimizacaoPage(): JSX.Element {
  const canExecute = useCapability('lgpd-anonymization-execute');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canExecute) {
    return (
      <div className="anon-scope anon-page" style={{ padding: 32 }}>
        <CapabilityLockedBanner
          capability="lgpd-anonymization-execute"
          tierMin="professional"
          fallbackMessage="O wizard de anonimização é restrito ao DPO (pacote Professional ou superior). Anonimização de todo o tenant é feita via solicitação LGPD formal no Centro de Direitos."
        />
      </div>
    );
  }

  return <AnonimizacaoPageInner />;
}

function AnonimizacaoPageInner(): JSX.Element {
  const [form, setForm] = useState<AnonymizationFormState>(initialAnonymizationForm);
  const [activeEntity, setActiveEntity] = useState<EntityRef | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [executeTarget, setExecuteTarget] = useState<AnonymizationWireItem | null>(null);

  const { data: history, isLoading, isError } = useAnonymizationsByEntity(activeEntity);
  const create = useCreateAnonymization();
  const execute = useExecuteAnonymization();
  const complete = useCompleteAnonymization();
  const preview = usePreviewAnonymization();

  const validationError = validateAnonymizationForm(form);
  const isBusy = create.isPending || execute.isPending || complete.isPending;

  function patch(p: Partial<AnonymizationFormState>): void {
    setForm((prev) => ({ ...prev, ...p }));
    // Plano é função de (entidade, nível) — qualquer mudança o invalida.
    preview.reset();
  }

  // Dry-run v1 cobre entityType='person' (BE responde 400 honesto p/ demais).
  const canPreview = form.entityType.trim() === 'person' && form.entityId.trim() !== '';

  function handlePreview(): void {
    if (!canPreview) return;
    preview.mutate({
      entityType: form.entityType.trim(),
      entityId: form.entityId.trim(),
      level: form.level,
    });
  }

  function loadHistory(): void {
    if (form.entityType.trim() === '' || form.entityId.trim() === '') return;
    setActiveEntity({ entityType: form.entityType.trim(), entityId: form.entityId.trim() });
  }

  function handleCreate(): void {
    if (validationError !== null) return;
    setCreateError(null);
    create.mutate(buildCreateRequest(form), {
      onSuccess: () => {
        // Garante que o histórico aponta para a entidade recém-criada.
        setActiveEntity({ entityType: form.entityType.trim(), entityId: form.entityId.trim() });
        patch({ documentReferenceId: '' });
      },
      onError: (err) => {
        setCreateError(err.message !== '' ? err.message : 'Não foi possível criar a anonimização.');
      },
    });
  }

  function confirmExecute(): void {
    if (executeTarget === null) return;
    setActionError(null);
    execute.mutate(executeTarget.id, {
      onSuccess: () => setExecuteTarget(null),
      onError: (err) => {
        setActionError(err.message !== '' ? err.message : 'Falha ao executar a anonimização.');
      },
    });
  }

  function handleComplete(item: AnonymizationWireItem): void {
    setActionError(null);
    complete.mutate(
      { id: item.id, body: { success: true } },
      {
        onError: (err) => {
          setActionError(err.message !== '' ? err.message : 'Falha ao concluir a anonimização.');
        },
      },
    );
  }

  const pendingCount = useMemo(
    () => history.filter((a) => a.status === 'pending' || a.status === 'in_progress').length,
    [history],
  );

  return (
    <div className="anon-scope anon-page">
      <header className="anon-pagehead">
        <div>
          <h1 className="anon-pagehead__title">Anonimização</h1>
          <p className="anon-pagehead__sub">
            Anonimização granular por entidade — 3 níveis canônicos com trade-off entre proteção do
            titular e preservação da auditoria. <strong>Anonimização ≠ deleção real</strong> e é{' '}
            <strong>irreversível</strong>. Para eliminação total formal, use uma solicitação LGPD no
            Centro de Direitos.
          </p>
        </div>
      </header>

      <section className="anon-card">
        <h2 className="anon-card__title">1 · Entidade-alvo</h2>
        <div className="anon-form-grid">
          <label className="anon-field">
            <span className="anon-field__label">Tipo de entidade</span>
            <select
              className="anon-select"
              value={form.entityType}
              onChange={(e) => patch({ entityType: e.target.value })}
            >
              {ENTITY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="anon-field__hint">
              {ENTITY_TYPE_OPTIONS.find((o) => o.value === form.entityType)?.hint}
            </span>
          </label>
          <label className="anon-field">
            <span className="anon-field__label">
              ID da entidade <span className="anon-defer-tag">busca por nome em breve</span>
            </span>
            <input
              className="anon-input"
              placeholder="person_… · bond_… · etc."
              value={form.entityId}
              onChange={(e) => patch({ entityId: e.target.value })}
            />
          </label>
        </div>
        <button
          type="button"
          className="anon-btn anon-btn--ghost"
          disabled={form.entityType.trim() === '' || form.entityId.trim() === ''}
          onClick={loadHistory}
        >
          Carregar histórico desta entidade
        </button>
      </section>

      <section className="anon-card">
        <h2 className="anon-card__title">2 · Nova anonimização</h2>
        <div className="anon-field">
          <span className="anon-field__label">Nível de anonimização</span>
          <div className="anon-radio-group" role="radiogroup" aria-label="Nível de anonimização">
            {LEVELS.map((lvl) => (
              <label
                key={lvl}
                className={`anon-radio-item${form.level === lvl ? ' anon-radio-item--checked' : ''}`}
              >
                <input
                  type="radio"
                  name="anon-level"
                  className="anon-radio-item__input"
                  checked={form.level === lvl}
                  onChange={() => patch({ level: lvl })}
                />
                <span>
                  <strong className="anon-radio-item__title">
                    {ANONYMIZATION_LEVEL_META[lvl].label}
                  </strong>
                  <span className="anon-radio-item__hint">
                    {ANONYMIZATION_LEVEL_META[lvl].hint}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <label className="anon-field">
          <span className="anon-field__label">
            Referência do documento (base legal) <span className="anon-defer-tag">opcional</span>
          </span>
          <input
            className="anon-input"
            placeholder="doc_… ou referência processual"
            value={form.documentReferenceId}
            onChange={(e) => patch({ documentReferenceId: e.target.value })}
          />
        </label>

        <div className="anon-card__foot">
          <button
            type="button"
            className="anon-btn anon-btn--ghost"
            disabled={!canPreview || preview.isPending}
            onClick={handlePreview}
          >
            {preview.isPending ? 'Computando plano…' : 'Pré-visualizar impacto (dry-run)'}
          </button>
          {!canPreview && (
            <span className="anon-foot-hint">
              Dry-run disponível para Pessoa específica (informe o ID). Demais tipos: fatia futura.
            </span>
          )}
        </div>

        {preview.isError && (
          <p className="anon-alert anon-alert--error" role="alert">
            {preview.error.message !== ''
              ? preview.error.message
              : 'Falha ao computar o plano de anonimização.'}
          </p>
        )}

        {preview.data !== undefined && <AnonymizationPreviewPanel plan={preview.data} />}

        <div className="anon-defer-panel">
          Co-signatário Regulated e configuração de auditoria (retenção/notificação) —{' '}
          <strong>em breve</strong>. A criação registra a anonimização como <em>pendente</em>; a
          execução (irreversível) exige confirmação na lista abaixo.
        </div>

        {createError !== null && (
          <p className="anon-alert anon-alert--error" role="alert">
            {createError}
          </p>
        )}

        <div className="anon-card__foot">
          <button
            type="button"
            className="anon-btn anon-btn--primary"
            disabled={validationError !== null || isBusy}
            onClick={handleCreate}
          >
            {create.isPending ? 'Criando…' : 'Criar anonimização (pendente)'}
          </button>
          {validationError !== null && <span className="anon-foot-hint">{validationError}</span>}
        </div>
      </section>

      <section className="anon-card">
        <h2 className="anon-card__title">
          3 · Histórico da entidade
          {activeEntity !== null && (
            <span className="anon-card__title-meta">
              {activeEntity.entityType} · {activeEntity.entityId} · {pendingCount} em aberto
            </span>
          )}
        </h2>

        {actionError !== null && (
          <p className="anon-alert anon-alert--error" role="alert">
            {actionError}
          </p>
        )}

        {activeEntity === null ? (
          <p className="anon-muted">
            Informe uma entidade e carregue o histórico para ver e executar anonimizações.
          </p>
        ) : isLoading ? (
          <p className="anon-muted">Carregando histórico…</p>
        ) : isError ? (
          <p className="anon-alert anon-alert--error" role="alert">
            Falha ao carregar o histórico desta entidade.
          </p>
        ) : history.length === 0 ? (
          <p className="anon-muted">Nenhuma anonimização registrada para esta entidade.</p>
        ) : (
          <div className="anon-list">
            {history.map((item) => (
              <AnonymizationHistoryRow
                key={item.id}
                item={item}
                onExecute={setExecuteTarget}
                onComplete={handleComplete}
                isBusy={isBusy}
              />
            ))}
          </div>
        )}
      </section>

      {executeTarget !== null && (
        <ExecuteAnonymizationModal
          item={executeTarget}
          onClose={() => {
            setExecuteTarget(null);
            setActionError(null);
          }}
          onConfirm={confirmExecute}
          isSubmitting={execute.isPending}
          error={actionError}
        />
      )}
    </div>
  );
}
